import { html, LitElement } from 'lit-element'
import { repeat } from 'lit-html/directives/repeat'
import { getLocationSearchParamsByName } from '../../../common/dom-helpers'
import { t } from '../../../directives/translate'
import type { GlobalSearchResult } from '../../../extensions'
import { executeCommand } from '../../../services/namespaces'
import { getGlobalSearchManager } from '../global-search-manager'
import type { GlobalSearchCategoryUI, GlobalSearchColumnUI } from '../global-search-models'
import searchResultsPageStyles from './search-results-page.styles.scss'

export interface CategorySection {
  readonly category: GlobalSearchCategoryUI
  // eslint-disable-next-line functional/prefer-readonly-type
  readonly results: GlobalSearchResult[]
}

export class SearchResultsPage extends LitElement {
  static readonly tagName = 'goto-search-results-page'
  private searchQuery = ''
  private searchResults: readonly GlobalSearchResult[] = []
  private categorySections: CategorySection[] = []

  static get styles() {
    return searchResultsPageStyles
  }

  async connectedCallback() {
    super.connectedCallback()
    this.searchQuery = getLocationSearchParamsByName('query') ?? ''
    await this.executeSearch()
    this.setCategorySections()
  }

  private async executeSearch() {
    this.searchResults = await getGlobalSearchManager().searchFor(this.searchQuery)
  }

  private setCategorySections() {
    this.searchResults.forEach(result => {
      const category = getGlobalSearchManager().getCategory(result)
      if (category) {
        const categorySection = this.categorySections.find(categorySection => categorySection.category === category)
        if (categorySection) {
          categorySection.results.push(result)
        } else {
          this.categorySections.push({ category: category, results: [result] })
        }
      }
    })
    this.requestUpdate()
  }

  private executeDefaultActionCommand(result: GlobalSearchResult) {
    const defaultAction = getGlobalSearchManager().getResultDefaultAction(result, 'list')
    const params = [result, this.searchQuery]
    if (defaultAction?.command) {
      executeCommand(defaultAction.command, [...params])
    }
  }

  private renderTableHeader(columns: readonly GlobalSearchColumnUI[]) {
    return html`<chameleon-data-table-row>
      ${repeat(
        columns,
        column => column.id,
        column =>
          html`<chameleon-data-table-cell class="column-header">${column.displayName}</chameleon-data-table-cell>`,
      )}
    </chameleon-data-table-row>`
  }

  private renderTableBody(results: GlobalSearchResult[], columns: readonly GlobalSearchColumnUI[]) {
    return html`${repeat(
      results,
      result =>
        html`<chameleon-data-table-row
          class="table-body-data-row"
          @click=${() => this.executeDefaultActionCommand(result)}
          >${this.renderDataTableCells(result, columns)}</chameleon-data-table-row
        >`,
    )} `
  }

  private renderDataTableCells(result: GlobalSearchResult, columns: readonly GlobalSearchColumnUI[]) {
    return html`${repeat(
      columns,
      column => column.id,
      column =>
        html`<chameleon-data-table-cell class="table-body-data-cells"
          >${getGlobalSearchManager().getResultFieldValue(result, column.valueField)}</chameleon-data-table-cell
        >`,
    )}`
  }

  render() {
    return html`<chameleon-typography variant="heading-small" class="search-query-heading"
        >${t('Search results for')} '${this.searchQuery}'</chameleon-typography
      >
      <div class="results-table">
        <chameleon-data-table>
          ${repeat(
            this.categorySections,
            categorySection => categorySection.category.id,
            categorySection =>
              html`<chameleon-data-table-header
                  >${this.renderTableHeader(categorySection.category.columns)}</chameleon-data-table-header
                ><chameleon-data-table-body
                  >${this.renderTableBody(
                    categorySection.results,
                    categorySection.category.columns,
                  )}</chameleon-data-table-body
                >`,
          )}
        </chameleon-data-table>
      </div>`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [SearchResultsPage.tagName]: SearchResultsPage
  }
}
